import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Table, Modal, Button, Checkbox } from 'semantic-ui-react';

const ALL_COMPANIES = '';

const AllUsersListModal = observer(({
    usersStore,
    open,
    onClose,
    onAdd,
    currentCompanyId,
    adminStore,
    managerOptions = [],
}) => {
    // Selection is tracked by userId, not node reference: the fetch below
    // replaces usersStore.allUsers wholesale (no MST identifier on User), so
    // node refs captured before it resolves would point at destroyed nodes.
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [sourceCompanyId, setSourceCompanyId] = useState(ALL_COMPANIES);
    const [reportsToId, setReportsToId] = useState('');
    const [fetchError, setFetchError] = useState('');

    // Scope = every company the acting admin belongs to, plus the one being
    // viewed. Scoping to the CURRENT company alone (as this did between
    // b02fa4d4 and now) makes the picker structurally useless: it can then only
    // list people who are already members, which is exactly the set you can
    // never add. The server intersects these ids with the caller's own
    // companies (companies/getUsersByCompanyIds.js), so we can never request
    // more than the caller is entitled to.
    //
    // A platform admin has no companyIds, so this collapses to the current
    // company. That is deliberate — fanning out over adminStore.companies would
    // scan the whole users table for every tenant. Admins use the
    // duplicate-email recovery flow for cross-tenant adds.
    const scopeCompanyIds = Array.from(new Set([
        ...(usersStore.currentUser?.companyIds || []),
        ...(currentCompanyId ? [currentCompanyId] : []),
    ]));
    const scopeKey = scopeCompanyIds.join(',');

    useEffect(() => {
        if (!open || !scopeKey) return;
        // getApi throws on failure; without a catch the modal would render its
        // empty state over stale data — indistinguishable from "nobody to add".
        usersStore.getUsersForCompanies(scopeKey.split(',')).catch((err) => {
            console.error('Failed to load users for the picker:', err);
            setFetchError(err?.message || 'Failed to load users. Please close and try again.');
        });
    }, [usersStore, open, scopeKey]);

    // Clear per-session choices every time the modal opens so a cancelled
    // selection never leaks into the next one.
    useEffect(() => {
        if (!open) return;
        setSelectedUserIds([]);
        setSourceCompanyId(ALL_COMPANIES);
        setReportsToId('');
        setFetchError('');
    }, [open]);

    const companyNameById = {};
    (adminStore?.companies || []).forEach(entry => {
        if (entry?.company?.id) {
            companyNameById[entry.company.id] = entry.company.name;
        }
    });

    // Companies a candidate can be sourced FROM: the admin's own, minus the one
    // being viewed. Only those we can name are offered as a filter.
    const sourceCompanies = scopeCompanyIds
        .filter(id => id !== currentCompanyId && companyNameById[id])
        .map(id => ({ id, name: companyNameById[id] }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const showCompanyColumn = sourceCompanies.length > 0;

    // Only ever name companies inside the admin's own scope, so a user who also
    // belongs to an unrelated tenant never leaks that tenant's name.
    const companyLabel = (user) => (user.companyIds || [])
        .filter(id => id !== currentCompanyId && companyNameById[id] && scopeCompanyIds.includes(id))
        .map(id => companyNameById[id])
        .sort((a, b) => a.localeCompare(b))
        .join(', ');

    const candidates = usersStore.allUsers.filter(user => {
        // Already a member of the company being viewed — nothing to add.
        if ((user.companyIds || []).includes(currentCompanyId)) return false;
        if (sourceCompanyId !== ALL_COMPANIES && !(user.companyIds || []).includes(sourceCompanyId)) return false;
        return true;
    });

    const handleUserSelect = (user) => {
        setSelectedUserIds(prev =>
            prev.includes(user.userId)
                ? prev.filter(id => id !== user.userId)
                : [...prev, user.userId]
        );
    };

    const renderTableRows = () => {
        if (fetchError) {
            return (
                <Table.Row>
                    <Table.Cell colSpan={showCompanyColumn ? 5 : 4} textAlign='center' style={{ color: '#db2828' }}>
                        {fetchError}
                    </Table.Cell>
                </Table.Row>
            );
        }

        if (usersStore.isLoading) {
            return (
                <Table.Row>
                    <Table.Cell colSpan={showCompanyColumn ? 5 : 4} textAlign='center' style={{ color: '#767676' }}>
                        Loading users…
                    </Table.Cell>
                </Table.Row>
            );
        }

        if (candidates.length === 0) {
            return (
                <Table.Row>
                    <Table.Cell colSpan={showCompanyColumn ? 5 : 4} textAlign='center' style={{ color: '#767676' }}>
                        No users available to add.
                    </Table.Cell>
                </Table.Row>
            );
        }

        return candidates.map((user, i) => (
            <Table.Row key={user.userId || i}>
                <Table.Cell textAlign='center'>
                    <Checkbox
                        checked={selectedUserIds.includes(user.userId)}
                        onChange={() => handleUserSelect(user)}
                    />
                </Table.Cell>
                <Table.Cell>{user.firstName}</Table.Cell>
                <Table.Cell>{user.lastName}</Table.Cell>
                <Table.Cell>{user.email}</Table.Cell>
                {showCompanyColumn && <Table.Cell>{companyLabel(user)}</Table.Cell>}
            </Table.Row>
        ));
    };

    const handleClose = () => {
        onClose();
    };

    const handleAdd = () => {
        // Resolve ids against the CURRENT store nodes at click time.
        const selected = usersStore.allUsers.filter(u => selectedUserIds.includes(u.userId));
        onAdd(selected, reportsToId || undefined);
        setSelectedUserIds([]);
    };

    const selectStyle = {
        padding: '6px 10px',
        fontSize: '13px',
        border: '1px solid rgba(34,36,38,.15)',
        borderRadius: '4px',
        fontFamily: 'inherit',
        backgroundColor: '#fff',
    };

    return (
        <Modal open={open} onClose={handleClose} size={'small'}>
            <Modal.Header>Select Users</Modal.Header>
            <Modal.Content style={{ textAlign: 'center' }}>
                {showCompanyColumn && (
                    <div style={{ textAlign: 'left', marginBottom: '12px' }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, marginRight: '8px' }}>From</label>
                        <select
                            data-testid="source-company-filter"
                            style={selectStyle}
                            value={sourceCompanyId}
                            onChange={(e) => setSourceCompanyId(e.target.value)}
                        >
                            <option value={ALL_COMPANIES}>All my companies</option>
                            {sourceCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                )}
                <Table celled fixed style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell textAlign='center' width={1}></Table.HeaderCell>
                            <Table.HeaderCell>First Name</Table.HeaderCell>
                            <Table.HeaderCell >Last Name</Table.HeaderCell>
                            <Table.HeaderCell>Email</Table.HeaderCell>
                            {showCompanyColumn && <Table.HeaderCell>Company</Table.HeaderCell>}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {renderTableRows()}
                    </Table.Body>
                </Table>
                {managerOptions.length > 0 && (
                    <div style={{ textAlign: 'left' }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, marginRight: '8px' }}>Reports To</label>
                        <select
                            data-testid="reports-to-select"
                            style={selectStyle}
                            value={reportsToId}
                            onChange={(e) => setReportsToId(e.target.value)}
                        >
                            {/* Their existing manager may not be a member of this
                                company, in which case the grid shows a blank
                                "Reports To" until it is set here or inline. */}
                            <option value="">Leave unchanged</option>
                            {managerOptions.map(opt => <option key={opt.key} value={opt.value}>{opt.text}</option>)}
                        </select>
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose} secondary>
                    Cancel
                </Button>
                <Button onClick={handleAdd} primary disabled={selectedUserIds.length === 0}>
                    Add
                </Button>
            </Modal.Actions>
        </Modal>
    );
});

export default AllUsersListModal;
